import { StandardRequest, StandardResponse } from "./functions";

/**
 * Source trigger for how an entity relationship link was created.
 * Strictly uses lowercase enumerations.
 */
export type EntityLinkSource = "description" | "manual";

/**
 * Relationship metadata for a contact associated with a group, memory, or media asset.
 */
export interface IEntityContactMeta {
  contactId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
  role?: "member" | "admin";
}

/**
 * Relationship metadata for a group associated with a group (child group), memory, or media asset.
 */
export interface IEntityGroupMeta {
  groupId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
}

/**
 * Relationship metadata for a memory associated with a contact or group.
 */
export interface IEntityMemoryMeta {
  memoryId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
}

/**
 * Relationship metadata for a media asset associated with a memory, contact, or group.
 */
export interface IEntityMediaMeta {
  mediaId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
}

/**
 * Relationship metadata for a place associated with a memory, media, contact, or group.
 */
export interface IEntityPlaceMeta {
  placeId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
}

/**
 * Relationship metadata for an anchor associated with a memory, media, contact, group, or place.
 */
export interface IEntityAnchorMeta {
  anchorId: string;
  addedAt: number;
  addedBy: string;
  source?: EntityLinkSource;
}

/**
 * Request payload to link two entities bidirectionally.
 */
export type LinkEntityRequest = StandardRequest & {
  sourceType: "memory" | "media" | "contact" | "group" | "place" | "anchor";
  sourceId: string;
  targetType: "memory" | "media" | "contact" | "group" | "place" | "anchor";
  targetId: string;
  source?: EntityLinkSource;
};

/**
 * Response payload after linking two entities.
 */
export type LinkEntityResponse = StandardResponse & {
  linked?: boolean;
};

/**
 * Request payload to unlink two entities bidirectionally.
 */
export type UnlinkEntityRequest = StandardRequest & {
  sourceType: "memory" | "media" | "contact" | "group" | "place" | "anchor";
  sourceId: string;
  targetType: "memory" | "media" | "contact" | "group" | "place" | "anchor";
  targetId: string;
};

/**
 * Response payload after unlinking two entities.
 */
export type UnlinkEntityResponse = StandardResponse & {
  unlinked?: boolean;
};

/**
 * Universal entity accessor for title or display name.
 * Resolves title across Memory (title), Media (title), Contact (displayName), and Group (name).
 *
 * @param {any} [entity] - Any entity with title, displayName, or name.
 * @param {string} [fallback="Untitled"] - Fallback string if no title is present.
 * @returns {string} The resolved title.
 */
export function getEntityTitle(
  entity?: { title?: string; displayName?: string; name?: string } | null,
  fallback = "Untitled"
): string {
  if (!entity) return fallback;
  return entity.title || entity.displayName || entity.name || fallback;
}

/**
 * Universal entity accessor for description or summary narrative.
 *
 * @param {any} [entity] - Any entity with optional description.
 * @returns {string} The description or empty string.
 */
export function getEntityDescription(
  entity?: { description?: string } | null
): string {
  return entity?.description || "";
}

/**
 * Universal entity accessor for photo / cover media ID.
 *
 * @param {any} [entity] - Any entity with optional photoMediaId or id.
 * @returns {string | undefined} The photo media ID.
 */
export function getEntityPhotoMediaId(
  entity?: { photoMediaId?: string; id?: string } | null
): string | undefined {
  if (!entity) return undefined;
  return entity.photoMediaId;
}
